import { asSchemaDefinition, SchemaFieldsWithContext } from '@sw-sw/lib-form';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import React, { useContext, useState, useEffect } from 'react';
import { Link, Redirect, useLocation } from 'react-router-dom';
import AuthenticationContext from '../../../contexts/AuthenticationContext';
import FormActions from '../../Shared/form/modal/FormActions';
import PasswordValidation, {
  isPasswordValid,
} from '../../Shared/PasswordValidation/PasswordValidation';
import SuccessModal from '../../Shared/SuccessModal/SuccessModal';

const LoginForm = () => {
  const authStore = useContext(AuthenticationContext);

  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);

  const [step, setStep] = useState(0);
  const [tenants, setTenants] = useState<any>([]);
  const [tenantInfo, setTenantInfo] = useState<any>(null);
  const [userCredentials, setUserCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [identifier, setIdentifier] = useState<string | null>(null);
  const [selectedVerificationMethod, setSelectedVerificationMethod] = useState<
    'sms' | 'email'
  >('email');
  const [resendTimer, setResendTimer] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [redirectToDashboard, setRedirectToDashboard] = useState(false);
  const [uuidToken, setUuidToken] = useState<string | null>(null);

  useEffect(() => {
    if (
      window.location.host.split('.').length > 1 &&
      window.location.origin !== process.env.REACT_APP_LOGIN_URL
    ) {
      const tenantName = window.location.host.split('.')[0];

      getTenant(tenantName);
    }
  }, [window.location.host]);

  // Timer effect for resend code countdown
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [resendTimer]);

  const getTenant = async (name: string) => {
    try {
      const tenantData = await authStore.getTenantByName(name);

      if (!tenantData) {
        window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
      }
      setTenantInfo(tenantData);
    } catch (error) {
      window.location.href = `${process.env.REACT_APP_LOGIN_URL}/login`;
    }
  };

  const handleVerify = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    // Store credentials for later use
    setUserCredentials({ email, password });
    if (!tenantInfo) {
      const tenantsData = await authStore.verifyUser(email, password);

      setTenants(tenantsData);
      setStep(1);
    } else {
      // If tenant is already known, check if device is remembered
      if (tenantInfo.mfa_enabled) {
        if (!tenantInfo.mfa_required) {
          // Skip MFA if device is remembered
          await handleLogin({ email, password, tenant: tenantInfo.domain });
        } else {
          setStep(2);
        }
      } else {
        await handleLogin({ email, password, tenant: tenantInfo.domain });
      }
    }
  };

  const handleTenantSelection = async ({ tenant }: { tenant: string }) => {
    // Store the selected tenant and move to verification method selection
    const selectedTenant = tenants.find((t: any) => t.domain === tenant);
    if (selectedTenant) {
      setTenantInfo(selectedTenant);
    }

    if (selectedTenant?.mfa_enabled) {
      if (!selectedTenant.mfa_required) {
        // Skip MFA if device is remembered
        await handleLogin({
          email: userCredentials!.email,
          password: userCredentials!.password,
          tenant: selectedTenant.domain,
        });
      } else {
        setStep(2);
      }
    } else {
      await handleLogin({
        email: userCredentials!.email,
        password: userCredentials!.password,
        tenant: selectedTenant.domain,
      });
    }
  };

  const handleSendVerificationCode = async ({
    verificationMethod: method,
  }: {
    verificationMethod: string;
  }) => {
    try {
      setSelectedVerificationMethod(method as 'sms' | 'email');
      const result = await authStore.sendVerificationCode(
        userCredentials?.email,
        method,
      );
      const identity = result?.identifier;
      setIdentifier(identity || null);
      // Start the 60-second timer
      setResendTimer(60);
      // Move to code verification step
      setStep(3);
    } catch (error) {
      console.error('Error sending verification code:', error);
      throw error;
    }
  };

  const handleVerifyCode = async ({
    verificationCode: code,
    rememberDevice,
  }: {
    verificationCode: string;
    rememberDevice?: boolean;
  }) => {
    try {
      const verificationResult = await authStore.verifyCode(
        userCredentials?.email,
        code,
        rememberDevice,
        selectedVerificationMethod,
      );
      if (!verificationResult) {
        throw new Error('Invalid verification code');
      }

      // If verification successful, proceed to login
      if (userCredentials && tenantInfo) {
        await handleLogin({
          email: userCredentials.email,
          password: userCredentials.password,
          tenant: tenantInfo.domain,
        });
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      throw error;
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return; // Prevent resend if timer is still active
    try {
      await authStore.sendVerificationCode(
        userCredentials?.email,
        selectedVerificationMethod,
      );
      // Restart the 60-second timer
      setResendTimer(60);
    } catch (error) {
      console.error('Error resending verification code:', error);
    }
  };

  const handleLogin = async ({
    email,
    password,
    tenant,
  }: {
    email: string;
    password: string;
    tenant: string;
  }) => {
    try {
      // datadogRum.addAction('login', {
      //   email,
      //   tenant,
      // });

      const token = await authStore.login(email, password, tenant);
      setUuidToken(token || null);
      const startUrl = process.env.REACT_APP_CLIENT_BASE_URL;
      if (isPasswordValid(password)) {
        if (token && startUrl) {
          const url = startUrl.split('//');

          window.location.href = `${url[0]}//${tenant}.${url[1]}?to=${
            urlSearchParams.get('to') || '/'
          }`;
        }
      } else {
        setStep(4);
      }
    } catch (error) {
      throw error;
    }
  };

  const handlePasswordChange = async (formData: { password: string }) => {
    await authStore.resetPassword(uuidToken, formData, () => {});
    setShowSuccess(true);
    setTimeout(() => setRedirectToDashboard(true), 5000);
    return;
  };

  if (authStore.loggedIn) {
    return <Redirect to={urlSearchParams.get('to') || '/'} />;
  }

  const schema = asSchemaDefinition({
    email: {
      label: 'Email',
      className: 'bottom-outline',
      autoComplete: 'username',
      validation: {
        required: true,
        format: 'email',
      },
    },
    password: {
      controlType: UIControlType.password,
      label: 'Password',
      className: 'bottom-outline',
      autoComplete: 'current-password',
      validation: {
        required: true,
      },
    },
  });

  const schema2 = asSchemaDefinition({
    tenant: {
      label: 'Account',
      controlType: UIControlType.select,
      validation: { required: true },
      options:
        tenants.length &&
        tenants.map((tenantData: any) => ({
          label: tenantData.name,
          value: tenantData.domain,
        })),
      className: 'bottogit pull origin release-multitenancym-outline',
    },
  });

  const schema3 = asSchemaDefinition({
    verificationMethod: {
      controlType: UIControlType.radio,
      validation: { required: true },
      options: [
        {
          label: `Email`,
          value: 'email',
        },
        {
          label: 'Text Message',
          value: 'sms',
        },
      ],
      className: 'verification-method-radio',
    },
  });

  const schema4 = asSchemaDefinition({
    verificationCode: {
      label: 'Verification Code',
      className: 'bottom-outline',
      placeholder: 'Enter 6-digit code',
      validation: {
        required: true,
        maxLength: 6,
      },
      autoComplete: 'off',
    },
    rememberDevice: {
      label: 'Remember this device for 7 days',
      controlType: UIControlType.checkbox,
      className: 'remember-device-checkbox',
    },
  });

  const schema5 = asSchemaDefinition({
    password: {
      controlType: UIControlType.password,
      label: 'New Password',
      className: 'bottom-outline',
      validation: {
        required: true,
        custom: (value: string) => {
          if (!value) return 'Password is required';
          if (!isPasswordValid(value)) {
            return 'Password does not meet all requirements';
          }
          return null;
        },
      },
      renderAfter: (fieldProps: any) => {
        return <PasswordValidation password={fieldProps.value || ''} />;
      },
    },
    confirm_password: {
      controlType: UIControlType.password,
      label: 'Confirm New Password',
      className: 'bottom-outline',
      validation: {
        required: true,
        custom: (value: string, allValues: any) => {
          if (!value) return 'Please confirm your password';
          if (value !== allValues.password) {
            return 'Passwords do not match';
          }
          return null;
        },
      },
    },
  });

  if (redirectToDashboard === true) {
    const startUrl = process.env.REACT_APP_CLIENT_BASE_URL;
    if (uuidToken && startUrl) {
      const url = startUrl.split('//');

      window.location.href = `${url[0]}//${tenantInfo.domain}.${url[1]}?to=${
        urlSearchParams.get('to') || '/'
      }`;
    }
  }

  if (step === 0) {
    return (
      <>
        <form
          onSubmit={() => handleVerify}
          className='login-form-main-container'
        >
          <div className='login-header'>
            <h2>Welcome to SW2</h2>
            <p>Please sign-in to your account</p>
          </div>
          <SchemaFieldsWithContext className='login-form' schema={schema}>
            <FormActions
              submitText='Login'
              onSubmit={handleVerify}
              onCancel={() => {}}
              showCancel={false}
              small={false}
              showSubmit={true}
              showErrorText={true}
              errorMessageText='Invalid form data. Please review errors above.'
              cancelText='Cancel'
            >
              <Link to='/forgot' className='no-underline forgot-password'>
                Forgot password
              </Link>
            </FormActions>
          </SchemaFieldsWithContext>
        </form>
      </>
    );
  }

  if (step === 1) {
    return (
      <form
        onSubmit={() => handleTenantSelection}
        className='login-form-main-container'
      >
        <div className='login-header'>
          <h2>Welcome to SW2</h2>
          <p>Please sign-in to your account</p>
        </div>
        <SchemaFieldsWithContext className='login-form1' schema={schema2}>
          <FormActions
            submitText='Continue'
            onSubmit={handleTenantSelection}
            onCancel={() => setStep(0)}
            showCancel={true}
            small={false}
            showSubmit={true}
            showErrorText={true}
            errorMessageText='Invalid form data. Please review errors above.'
            cancelText='Back'
          ></FormActions>
        </SchemaFieldsWithContext>
      </form>
    );
  }

  // Step 2: Request Verification Code
  if (step === 2) {
    return (
      <form
        onSubmit={() => handleSendVerificationCode}
        className='login-form-main-container'
      >
        <div className='login-header'>
          <h2>Multi-Factor Authentication (MFA)</h2>
          <p>
            For Security, your organization requires an additional verification
            step. Please complete the multi-factor authentication to continue.
          </p>
          <p>How would you like to receive your verification code?</p>
        </div>
        <SchemaFieldsWithContext className='login-form2' schema={schema3}>
          <FormActions
            submitText='Send Code'
            onSubmit={handleSendVerificationCode}
            onCancel={() => setStep(1)}
            showCancel={true}
            small={false}
            showSubmit={true}
            showErrorText={true}
            errorMessageText='Invalid form data. Please review errors above.'
            cancelText='Cancel'
          ></FormActions>
        </SchemaFieldsWithContext>
      </form>
    );
  }

  // Step 3: Verify Code
  if (step === 3) {
    return (
      <form
        onSubmit={() => handleVerifyCode}
        className='login-form-main-container'
      >
        <div className='login-header'>
          <h2>Enter Verification Code</h2>
          <p>
            Code sent by{' '}
            {selectedVerificationMethod === 'email' ? 'email' : 'text message'}{' '}
            to {identifier}
          </p>
        </div>
        <SchemaFieldsWithContext className='login-form3' schema={schema4}>
          <div className='resend-code-section'>
            <p>Didn't receive the code?</p>
            <button
              type='button'
              className='btn-link resend-code-button'
              onClick={handleResendCode}
              disabled={resendTimer > 0}
              style={{
                cursor: resendTimer > 0 ? 'not-allowed' : 'pointer',
                opacity: resendTimer > 0 ? 0.5 : 1,
              }}
            >
              <span className='resend-icon'>&#x21bb;</span>
              {resendTimer > 0
                ? ` Resend Code (${resendTimer}s)`
                : ' Resend Code'}
            </button>
          </div>

          <FormActions
            submitText='Verify Code'
            onSubmit={handleVerifyCode}
            onCancel={() => setStep(2)}
            showCancel={true}
            small={false}
            showSubmit={true}
            showErrorText={true}
            errorMessageText='Invalid form data. Please review errors above.'
            cancelText='Back'
          ></FormActions>
        </SchemaFieldsWithContext>
      </form>
    );
  }

  if (step === 4) {
    return (
      <>
        <form
          onSubmit={() => handlePasswordChange}
          className='login-form-main-container'
        >
          <div className='login-header'>
            <h2>Welcome to SW2</h2>
            <p>
              Your previous password does not meet the new security
              requirements. Please create a new password to continue.
            </p>
          </div>
          <SchemaFieldsWithContext className='login-form1' schema={schema5}>
            <FormActions
              submitText='Submit'
              onSubmit={handlePasswordChange}
              onCancel={() => setStep(0)}
              showCancel={true}
              small={false}
              showSubmit={true}
              showErrorText={true}
              errorMessageText='Invalid form data. Please review errors above.'
              cancelText='Back'
            ></FormActions>
          </SchemaFieldsWithContext>
        </form>
        <SuccessModal
          classes='reset-password'
          show={showSuccess}
          handleClose={() => setShowSuccess(false)}
          handleSubmit={() => {
            setShowSuccess(false);
            setRedirectToDashboard(true);
          }}
          title='Password Changed'
          message='Your password has been changed'
          submitBtnText='Go to Dashboard'
        />
      </>
    );
  }

  return <div></div>;
};

export default LoginForm;
